import { useEffect, useState, useRef } from "react";
import { useTranslation, Trans } from "react-i18next";
import { Star, Loader2, ExternalLink } from "lucide-react";
import { fetchTrustooConfig, loadTrustooScript, reinitTrustoo } from "./TrustooScoreBadge";
import { Reveal, MaskedLine } from "./motion/Reveal";

function Stars({ n = 5 }) {
  return (
    <div className="flex gap-0.5" aria-label={`${n}/5`}>
      {Array.from({ length: 5 }).map((_, i) => (
        <Star key={i} className={`w-4 h-4 ${i < n ? "fill-[#FF5A1F] text-[#FF5A1F]" : "text-slate-300"}`} />
      ))}
    </div>
  );
}

export default function Reviews() {
  const { t } = useTranslation();
  const [config, setConfig] = useState(null);
  const [error, setError] = useState(null);
  const containerRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    fetchTrustooConfig()
      .then((c) => { if (!cancelled) setConfig(c); })
      .catch(() => { if (!cancelled) setError("config"); });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (!config?.widget_token) return;
    let cancelled = false;
    loadTrustooScript("review")
      .then(() => {
        if (cancelled) return;
        reinitTrustoo();
        [80, 220, 500, 1200].forEach((ms) =>
          setTimeout(() => { if (!cancelled) reinitTrustoo(); }, ms)
        );
      })
      .catch(() => { if (!cancelled) setError("script"); });
    return () => { cancelled = true; };
  }, [config?.widget_token]);

  return (
    <section id="reviews" data-testid="reviews-section" className="section bg-white">
      <div className="max-w-7xl mx-auto px-5 md:px-10">
        <div className="grid lg:grid-cols-12 gap-10 items-end mb-12 md:mb-14">
          <div className="lg:col-span-7">
            <Reveal>
              <div className="flex items-center gap-3 mb-4">
                <span className="accent-bar" />
                <span className="text-[#FF5A1F] uppercase font-bold tracking-widest text-xs">
                  {t("reviews.eyebrow")}
                </span>
              </div>
            </Reveal>
            <h2 className="font-display text-4xl md:text-5xl lg:text-6xl text-[#0A1128] leading-[1.02]">
              <span className="block overflow-hidden"><MaskedLine text={t("reviews.title_1")} wordDelay={0.06} /></span>
              <span className="block overflow-hidden"><MaskedLine text={t("reviews.title_2")} wordDelay={0.05} delay={0.15} /></span>
            </h2>
            <Reveal delay={0.15}>
              <p className="text-base md:text-lg text-slate-600 leading-relaxed mt-5 max-w-xl">
                {t("reviews.subtitle")}
              </p>
            </Reveal>
          </div>
          <Reveal className="lg:col-span-5 lg:text-right" delay={0.2}>
            <div className="inline-flex items-center gap-4 border border-slate-200 p-4 md:p-5 bg-slate-50">
              <div className="flex flex-col">
                <Stars n={5} />
                <span className="text-xs font-bold uppercase tracking-wider text-slate-500 mt-1">
                  {t("reviews.source_label")}
                </span>
              </div>
              <div className="w-px h-10 bg-slate-300" />
              <a
                href="https://trustoo.nl"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-1.5 text-[#0A1128] hover:text-[#FF5A1F] transition-colors"
                data-testid="trustoo-link"
              >
                <span className="font-display text-sm md:text-base uppercase tracking-tight">Trustoo</span>
                <ExternalLink className="w-3.5 h-3.5" />
              </a>
            </div>
          </Reveal>
        </div>

        <div ref={containerRef} className="trustoo-widget-wrapper" data-testid="trustoo-widget-wrapper">
          {config?.widget_token ? (
            <div
              className="trustoo-review-widget"
              data-id={config.widget_token}
              data-testid="trustoo-review-widget"
              key={config.widget_token}
            />
          ) : !error ? (
            <div className="flex items-center justify-center py-16 text-slate-400" data-testid="trustoo-loading">
              <Loader2 className="w-6 h-6 animate-spin" />
            </div>
          ) : (
            <div className="border border-slate-200 bg-slate-50 p-8 text-center text-slate-500 text-sm" data-testid="trustoo-error">
              {t("reviews.fallback")}{" "}
              <a href="https://trustoo.nl" target="_blank" rel="noopener noreferrer" className="text-[#FF5A1F] font-bold hover:underline">trustoo.nl</a>.
            </div>
          )}
        </div>

        <p className="text-xs text-slate-400 text-center mt-8 max-w-2xl mx-auto">
          {t("reviews.footnote")}
        </p>
      </div>
    </section>
  );
}
